import { AlertTriangle } from 'lucide-react'
import { Markdown } from '~/components/ui/markdown'
import { TrendChart } from '~/features/monitoring/trend-chart'
import { FrequencyChart } from '~/features/monitoring/frequency-chart'
import type { DeviationDetail } from '~/api/types'

// recharts는 분석보드(board-cards)에서 이미 정적 번들되므로 여기서 lazy로 분리해도 이득이 없고,
// 배포로 청크 해시가 바뀌면 오래된 탭이 구 청크(404)를 로드하다 크래시하는 실패 모드만 생긴다.
// → 정적 임포트로 그 실패 모드를 제거한다.

/** 기준 텍스트에서 첫 숫자(기준선)를 추출. 차트의 기준선 표시용. */
function thresholdNumber(threshold?: string | null): number | null {
    if (!threshold) return null
    const m = threshold.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/)
    return m ? Number(m[0]) : null
}

type GuidanceCard = { title: string; body: string }

/**
 * guidance 문자열을 기준/관리/현장 3구간으로 분리.
 * "관리"·"현장" 헤딩을 못 찾으면 null을 반환(폴백 → 전체를 한 카드로 렌더).
 */
export function splitGuidance(guidance: string): GuidanceCard[] | null {
    const mgmt = guidance.match(/\n관리\s*\(/)
    const site = guidance.match(/\n현장\s*\(/)
    if (mgmt?.index == null || site?.index == null) return null

    // 헤딩 라벨은 떼고 본문만 추린다.
    const trim = (block: string) =>
        block.replace(/^\s*\S+\s*\([^)]*\)\s*\n?/, '').trim()

    return [
        { title: '기준', body: trim(guidance.slice(0, mgmt.index)) },
        { title: '관리', body: trim(guidance.slice(mgmt.index, site.index)) },
        { title: '현장', body: trim(guidance.slice(site.index)) },
    ]
}

/** 지표 상세 — 측정값 추이 차트 + 위험 평가항목 + 기준 해설·대응 근거. */
export function IndicatorDetail({ detail }: { detail: DeviationDetail }) {
    const th = thresholdNumber(detail.threshold)
    // 빈도/주기 지표는 값 임계선 추이가 아니라 기간별 '이행 횟수' 히스토그램으로 본다.
    const isFrequency = detail.mode === 'frequency'

    return (
        <div className='grid min-w-0 gap-4'>
            <section className='border-border bg-card rounded-md border p-5'>
                <h3 className='text-foreground mb-3 text-sm font-semibold'>
                    {isFrequency
                        ? '이행 횟수 추이 (기간별)'
                        : '측정값 추이 (기준선 대비)'}
                </h3>
                {isFrequency ? (
                    <>
                        <FrequencyChart series={detail.series} />
                        <p className='text-muted-foreground mt-2 text-xs'>
                            기준: {detail.threshold ?? '—'} · 기간 내{' '}
                            {detail.actualCount ?? detail.series.length}회 이행
                        </p>
                    </>
                ) : (
                    <>
                        <TrendChart
                            series={detail.series}
                            threshold={th}
                            unit={detail.unit}
                        />
                        <p className='text-muted-foreground mt-2 text-xs'>
                            기준: {detail.threshold ?? '—'} · 최근값{' '}
                            {detail.lastValue}
                            {detail.unit ? ` ${detail.unit}` : ''} · 점{' '}
                            {detail.series.length}개
                        </p>
                    </>
                )}
            </section>

            <section className='border-border bg-card rounded-md border p-5'>
                <div className='flex items-start gap-2'>
                    <AlertTriangle className='text-warning mt-0.5 size-4 shrink-0' />
                    <div className='min-w-0'>
                        <h3 className='text-foreground text-sm font-semibold'>
                            위험 평가항목
                        </h3>
                        <p className='text-muted-foreground mt-1 text-sm leading-6 break-words'>
                            {detail.itemTitle}
                        </p>
                    </div>
                </div>
                {detail.guidance ? (
                    <div className='border-border mt-3 border-t pt-3'>
                        <p className='text-foreground text-sm font-semibold'>
                            기준 해설 · 대응 근거
                        </p>
                        {(() => {
                            const cards = splitGuidance(detail.guidance)
                            if (cards) {
                                return (
                                    <div className='mt-2 grid gap-3 md:grid-cols-3'>
                                        {cards.map((c) => (
                                            <div
                                                key={c.title}
                                                className='border-border bg-card min-w-0 rounded-md border p-4'>
                                                <p className='text-primary text-sm font-semibold'>
                                                    {c.title}
                                                </p>
                                                <Markdown className='mt-1 break-words'>
                                                    {c.body}
                                                </Markdown>
                                            </div>
                                        ))}
                                    </div>
                                )
                            }
                            return (
                                <div className='border-border bg-card mt-2 min-w-0 rounded-md border p-4'>
                                    <Markdown className='break-words'>
                                        {detail.guidance}
                                    </Markdown>
                                </div>
                            )
                        })()}
                        <p className='text-muted-foreground mt-2 text-xs italic'>
                            근거: HACCP 평가 매뉴얼 체크리스트 세부내용
                        </p>
                    </div>
                ) : null}
            </section>
        </div>
    )
}
